import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {X} from "lucide-react";
import {createUser, normalizePhoneNumber} from "./api";
import {mapUserApiError} from "./errors";
import "../../shared/finance/financeOperations.css";
import {useTranslation} from "../../shared/i18n";


const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

export const UserCreateDialog = ({open, onCancel, onCreated, countryName, countryId}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const invokingElementRef = useRef(null);
    const submittingRef = useRef(false);
    const [form, setForm] = useState({name: "", phoneNumber: ""});
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        setForm({name: "", phoneNumber: ""});
        setError(null);
        setPending(false);
        submittingRef.current = false;
        const frame = window.requestAnimationFrame(() => firstFieldRef.current?.focus());
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!submittingRef.current) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open]);

    if (!open) return null;

    const submit = async (event) => {
        event.preventDefault();
        if (submittingRef.current) return;
        const name = form.name.trim();
        const phoneNumber = normalizePhoneNumber(form.phoneNumber);
        if (!name) {
            setError({message: t("sections.users.p1")});
            return;
        }
        if (!/^\+\d{10,15}$/.test(phoneNumber)) {
            setError({message: t("sections.users.p2")});
            return;
        }

        submittingRef.current = true;
        setPending(true);
        setError(null);
        try {
            const result = await createUser({name, phoneNumber, countryId});
            onCreated(result);
            setForm({name: "", phoneNumber: ""});
            onCancel();
        } catch (requestError) {
            setError(mapUserApiError(requestError));
        } finally {
            submittingRef.current = false;
            setPending(false);
        }
    };

    return createPortal(
        <div className="finance-dialog-backdrop">
            <section
                ref={dialogRef}
                className="finance-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
            >
                <form onSubmit={submit}>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{t("sections.users.p3")}</p>
                            <h2 id={titleId}>{t("sections.users.p4")}</h2>
                        </div>
                        <button
                            type="button"
                            className="finance-icon-button"
                            aria-label={t("sections.users.p5")}
                            title={t("sections.users.p5")}
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>
                    <p>
                        {t("sections.users.p6")} {countryName || (countryId ? t("sections.users.p7") : t("sections.users.p8"))}.
                    </p>
                    <div className="finance-dialog-fields">
                        <label>
                            <span>{t("sections.users.p9")}</span>
                            <input
                                ref={firstFieldRef}
                                value={form.name}
                                disabled={pending}
                                autoComplete="off"
                                onChange={(event) => {
                                    setForm((current) => ({...current, name: event.target.value}));
                                    setError(null);
                                }}
                            />
                        </label>
                        <label>
                            <span>{t("sections.users.p10")}</span>
                            <input
                                type="tel"
                                value={form.phoneNumber}
                                disabled={pending}
                                autoComplete="off"
                                onChange={(event) => {
                                    setForm((current) => ({...current, phoneNumber: event.target.value}));
                                    setError(null);
                                }}
                            />
                        </label>
                    </div>
                    {error?.message ? <div className="finance-dialog-error" role="alert">{error.message}</div> : null}
                    <footer className="finance-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={onCancel}>
                            {t("sections.users.p11")}
                        </button>
                        <button type="submit" className="admin-button admin-button-primary" disabled={pending}>
                            {pending ? t("sections.users.p12") : t("sections.users.p13")}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
